import React from 'react';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import type { TurnStep } from './turnState';

/** Steps saved on a finished answer (`metadata.agentcore.toolEvents`). */
export function savedSteps(metadata: any): TurnStep[] {
  const events = metadata?.agentcore?.toolEvents;
  if (!Array.isArray(events)) return [];
  return events
    .filter((e: any) => typeof e?.tool === 'string')
    .map((e: any, i: number) => ({
      id: String(e.toolUseId ?? i),
      tool: e.tool,
      label: typeof e.label === 'string' ? e.label : 'Working on it',
      status: e.status === 'failed' ? 'failed' : 'ok',
      durationMs: typeof e.durationMs === 'number' ? e.durationMs : undefined,
    }));
}

function StepRow({ step }: { step: TurnStep }) {
  return (
    <li className="flex items-center gap-2">
      {step.status === 'running' ? (
        <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-orange-500" aria-hidden="true" />
      ) : step.status === 'failed' ? (
        <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-500" aria-hidden="true" />
      ) : (
        <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-500" aria-hidden="true" />
      )}
      <span className={step.status === 'running' ? 'text-gray-700 dark:text-gray-200' : undefined}>
        {step.label}
        {step.status === 'running' ? '…' : ''}
      </span>
    </li>
  );
}

function FailedNotice({ steps }: { steps: TurnStep[] }) {
  const failed = Array.from(new Set(steps.filter((s) => s.status === 'failed').map((s) => s.label.toLowerCase())));
  if (!failed.length) return null;
  return (
    <p role="note" className="mt-2 flex items-start gap-1.5 rounded-md bg-amber-50 px-2 py-1.5 text-xs text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span>Some steps didn't work ({failed.join(', ')}), so this answer may be incomplete.</span>
    </p>
  );
}

/**
 * What AIVIA is doing, for every advisor. Live: each step as it runs. Saved: a collapsed summary.
 */
export function StepTimeline({ steps, live = false }: { steps: TurnStep[]; live?: boolean }) {
  if (!steps.length) return null;
  if (live) {
    return (
      <div className="mb-3 text-xs text-gray-500 dark:text-gray-400">
        <ol className="space-y-1" aria-live="polite" aria-label="Steps">
          {steps.map((s) => (
            <StepRow key={s.id} step={s} />
          ))}
        </ol>
        <FailedNotice steps={steps} />
      </div>
    );
  }
  return (
    <div className="mb-3 text-xs text-gray-500 dark:text-gray-400">
      <details>
        <summary className="cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200">
          {steps.length === 1 ? '1 step' : `${steps.length} steps`}
        </summary>
        <ol className="mt-1.5 space-y-1 pl-1">
          {steps.map((s) => (
            <StepRow key={s.id} step={s} />
          ))}
        </ol>
      </details>
      <FailedNotice steps={steps} />
    </div>
  );
}
